import { Button } from "@progress/kendo-react-buttons";
import { ComboBox, type ComboBoxChangeEvent, type ComboBoxFilterChangeEvent, type ListItemProps } from "@progress/kendo-react-dropdowns";
import { RadioGroup } from "@progress/kendo-react-inputs";
import { cloneElement, useEffect, useMemo, useRef, useState } from "react";
import { useLazySearchProductsQuery } from "../../api/productApi";
import { useLazySearchRetailersQuery } from "../../api/retailerApi";
import { useAppDispatch } from "../../app/hooks";
import type { ProductDto, RetailerDto } from "../../contracts";
import { getAgentResponseById, type CommercialSimulatorAgentResponse } from "../../models/agentResponseModels";
import { isCommercialSimulatorToolRequest, isCurrentMessageToolRequest, type CommercialSimulatorToolRequest } from "../../models/toolRequestModels";
import { getToolResponseById, type CommercialSimulatorToolResponse } from "../../models/toolResponseModels";
import { setCurrentCommercialPlan } from "../../slices/chatSlice";
import { useChatConversation } from "../context/useChatConversation";
import type { ToolComponentProps } from "./toolRenderer";

type SimulatorStep = 0 | 1 | 2;

type ProductSearchSuggestion = {
    id: number;
    product: ProductDto;
    display_name: string;
};

type SkuSelectionProps = {
    options: ProductDto[];
    retailerId: number;
    originalSkuName?: string;
    selectedProduct?: ProductDto | null;
    onChange: (product: ProductDto | null) => void;
};

type RetailerSelectionProps = {
    options: RetailerDto[];
    selectedRetailerId?: number;
    onChange: (retailerId: number) => void;
};

type FinalSummaryProps = {
    skuName?: string;
    retailerName?: string;
};

const getProductSuggestionDetails = (product: ProductDto): string => {
    return [
        product.brand,
        product.sub_brand,
        product.sku ? `SKU ${product.sku}` : null,
        product.barcode ? `Barcode ${product.barcode}` : null
    ].filter(Boolean).join(" | ");
};

const getProductSuggestionDisplayName = (product: ProductDto): string => {
    const details = getProductSuggestionDetails(product);

    return details ? `${product.name} (${details})` : product.name;
};

const toProductSearchSuggestion = (product: ProductDto): ProductSearchSuggestion => ({
    id: product.id,
    product,
    display_name: getProductSuggestionDisplayName(product)
});

type ActionButtonsProps = {
    isReadOnly: boolean;
    isPlanAvailable: boolean;
    isBusy: boolean;
    hasStartedSimulation: boolean;
    showBack: boolean;
    primaryAction?: {
        label: string;
        disabled: boolean;
        themeColor: "success" | "primary";
        onClick: () => void;
    };
    onBack: () => void;
    onCancel?: () => void;
    onOpenGeneratedPlan?: () => void;
};

const SkuSelection: React.FC<SkuSelectionProps> = ({
    options,
    retailerId,
    originalSkuName,
    selectedProduct,
    onChange
}) => {
    const hasSkuOptions = options.length > 0;
    const radioOptions = options.map(option => ({ label: option.name, value: option.id }));
    const [manualSearchText, setManualSearchText] = useState(selectedProduct ? getProductSuggestionDisplayName(selectedProduct) : "");
    const [manualSearchOptions, setManualSearchOptions] = useState<ProductSearchSuggestion[]>([]);
    const [isManualSearchLoading, setIsManualSearchLoading] = useState(false);
    const [manualSearchError, setManualSearchError] = useState(false);
    const lastSearchRequestId = useRef(0);
    const [searchProducts] = useLazySearchProductsQuery();

    useEffect(() => {
        setManualSearchText(selectedProduct ? getProductSuggestionDisplayName(selectedProduct) : "");
    }, [selectedProduct]);

    useEffect(() => {
        setManualSearchOptions([]);
        setManualSearchError(false);
        setIsManualSearchLoading(false);
        lastSearchRequestId.current += 1;
    }, [retailerId]);

    useEffect(() => {
        if (hasSkuOptions) {
            return;
        }

        const selectedProductSearchText = selectedProduct ? getProductSuggestionDisplayName(selectedProduct) : "";
        const searchTerm = manualSearchText.trim();

        if (searchTerm.length < 2 || searchTerm === selectedProductSearchText) {
            lastSearchRequestId.current += 1;
            setManualSearchOptions([]);
            setIsManualSearchLoading(false);
            setManualSearchError(false);
            return;
        }

        const requestId = lastSearchRequestId.current + 1;
        lastSearchRequestId.current = requestId;
        const timeoutId = window.setTimeout(() => {
            const searchManualProductsAsync = async (): Promise<void> => {
                setIsManualSearchLoading(true);
                setManualSearchError(false);

                try {
                    const productResponse = await searchProducts({
                        search_term: searchTerm,
                        retailer_id: retailerId,
                        limit: 10
                    }).unwrap();

                    if (lastSearchRequestId.current !== requestId) {
                        return;
                    }

                    setManualSearchOptions(productResponse.items.map(toProductSearchSuggestion));
                } catch {
                    if (lastSearchRequestId.current !== requestId) {
                        return;
                    }

                    setManualSearchError(true);
                    setManualSearchOptions([]);
                } finally {
                    if (lastSearchRequestId.current === requestId) {
                        setIsManualSearchLoading(false);
                    }
                }
            };

            searchManualProductsAsync();
        }, 300);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [hasSkuOptions, manualSearchText, retailerId, searchProducts, selectedProduct]);

    const handleManualSearchFilterChange = (event: ComboBoxFilterChangeEvent): void => {
        const searchText = event.filter.value;
        setManualSearchText(searchText);

        if (selectedProduct != null
            && searchText !== getProductSuggestionDisplayName(selectedProduct)) {
            onChange(null);
        }
    };
    const handleManualSearchSelectionChange = (event: ComboBoxChangeEvent): void => {
        const suggestion = event.value as ProductSearchSuggestion | null;

        onChange(suggestion?.product ?? null);
        setManualSearchText(suggestion?.display_name ?? "");
    };
    const renderSuggestion = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps): React.ReactNode => {
        const suggestion = itemProps.dataItem as ProductSearchSuggestion;

        return cloneElement(
            li,
            li.props,
            <div className="d-flex flex-column py-1">
                <span className="fw-semibold">{suggestion.product.name}</span>
                <span className="small text-muted">{getProductSuggestionDetails(suggestion.product)}</span>
            </div>
        );
    };

    const selectedSuggestion = selectedProduct == null
        ? null
        : toProductSearchSuggestion(selectedProduct);

    return (
        <div>
            <p className="fw-semibold mb-2">Select SKU</p>
            {!hasSkuOptions && (
                <div className="d-flex flex-column gap-3">
                    <div className="alert alert-warning mb-0" role="alert">
                        <span>
                            The original SKU
                            {originalSkuName ? ` "${originalSkuName}"` : ""}
                            {" could not be matched. Search by product name, SKU, or barcode to select a valid product."}
                        </span>
                    </div>
                    <div>
                        <ComboBox
                            data={manualSearchOptions}
                            textField="display_name"
                            dataItemKey="id"
                            value={selectedSuggestion}
                            filter={manualSearchText}
                            filterable={true}
                            allowCustom={false}
                            loading={isManualSearchLoading}
                            itemRender={renderSuggestion}
                            listNoDataRender={() => (
                                <div className="p-2 text-muted small">
                                    {manualSearchText.trim().length < 2
                                        ? "Enter at least 2 characters to search."
                                        : isManualSearchLoading
                                            ? "Searching products..."
                                            : "No matching products were found."}
                                </div>
                            )}
                            placeholder="Search product name, SKU, or barcode"
                            onFilterChange={handleManualSearchFilterChange}
                            onChange={handleManualSearchSelectionChange}
                        />
                        <div className="form-text">Select a product from the search results to continue.</div>
                        {manualSearchError && (
                            <div className="text-danger small mt-2">Unable to search products. Try again.</div>
                        )}
                        {selectedProduct == null && manualSearchText.trim().length >= 2 && !isManualSearchLoading && !manualSearchError && (
                            <div className="text-muted small mt-2">A valid product selection is required before continuing.</div>
                        )}
                    </div>
                </div>
            )}
            {hasSkuOptions && (
                <RadioGroup
                    className="d-flex flex-column gap-2"
                    data={radioOptions}
                    value={selectedProduct?.id}
                    onChange={event => onChange(options.find(option => option.id === event.value) ?? null)}
                    layout="vertical"
                />
            )}
        </div>
    );
};

const RetailerSelection: React.FC<RetailerSelectionProps> = ({ options, selectedRetailerId, onChange }) => {
    const hasRetailerOptions = options.length > 0;
    const radioOptions = options.map(option => ({ label: option.name, value: option.id }));

    return (
        <div>
            <p className="fw-semibold mb-2">Select Retailer</p>
            {!hasRetailerOptions && (
                <div className="alert alert-warning mb-0" role="alert">
                    No matching retailers were found.
                </div>
            )}
            {hasRetailerOptions && (
                <RadioGroup
                    className="d-flex flex-column gap-2"
                    data={radioOptions}
                    value={selectedRetailerId}
                    onChange={event => onChange(event.value as number)}
                    layout="vertical"
                />
            )}
        </div>
    );
};

const FinalSummary: React.FC<FinalSummaryProps> = ({ skuName, retailerName }) => (
    <div>
        <p className="fw-semibold mb-2">Final Summary</p>
        <div className="border rounded-3 bg-light p-3">
            <p className="text-muted small mb-1">SKU selected</p>
            <p className="fw-semibold mb-3">
                {skuName || "No SKU selected"}
            </p>
            <p className="text-muted small mb-1">Retailer selected</p>
            <p className="fw-semibold mb-0">
                {retailerName || "No retailer selected"}
            </p>
        </div>
    </div>
);

const ActionButtons: React.FC<ActionButtonsProps> = ({
    isReadOnly,
    isPlanAvailable,
    isBusy,
    hasStartedSimulation,
    showBack,
    primaryAction,
    onBack,
    onCancel,
    onOpenGeneratedPlan
}) => {
    if (isReadOnly && isPlanAvailable) {
        return onOpenGeneratedPlan
            ? (
                    <div className="d-flex justify-content-end mt-3">
                        <Button
                            type="button"
                            fillMode="outline"
                            themeColor="secondary"
                            size="small"
                            onClick={onOpenGeneratedPlan}
                        >
                            Open generated plan
                        </Button>
                    </div>
                )
            : null;
    }

    return (
        <div className="d-flex justify-content-between mt-4">
            <div className="d-flex align-items-center gap-2">
                {!hasStartedSimulation && (
                    <Button
                        type="button"
                        fillMode="outline"
                        themeColor="error"
                        disabled={isBusy}
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                )}

                {showBack && !hasStartedSimulation && (
                    <Button
                        type="button"
                        fillMode="outline"
                        onClick={onBack}
                    >
                        Back
                    </Button>
                )}
            </div>

            {primaryAction && !hasStartedSimulation && (
                <Button
                    type="button"
                    themeColor={primaryAction.themeColor}
                    disabled={primaryAction.disabled}
                    onClick={primaryAction.onClick}
                >
                    {primaryAction.label}
                </Button>
            )}
        </div>
    );
};

const PlanToolCallSteps: React.FC<ToolComponentProps> = ({
    message
}: ToolComponentProps) => {
    const {
        chatMessages,
        isChatBusy,
        actions
    } = useChatConversation();

    if (message?.id == null || !isCommercialSimulatorToolRequest(message.payload)) {
        throw new Error("Unknown message tool request type or state");
    }

    const toolRequest: CommercialSimulatorToolRequest = message.payload;

    const selectedOptions = getToolResponseById<CommercialSimulatorToolResponse>(chatMessages, toolRequest.id)?.tool_call_user_selection;
    const planResponse = getAgentResponseById<CommercialSimulatorAgentResponse>(chatMessages, toolRequest.id);

    const initialOptions = useMemo(() => {
        return toolRequest.input;
    }, [toolRequest]);

    const isToolCallInProgress = isCurrentMessageToolRequest(chatMessages);
    const isBusy = isChatBusy || !isToolCallInProgress;
    const isReadOnly = planResponse != null || selectedOptions != null;
    const dispatch = useAppDispatch();

    const [step, setStep] = useState<SimulatorStep>(isReadOnly ? 2 : 0);
    const [selectedSku, setSelectedSku] = useState<ProductDto | null>(null);
    const [selectedRetailerId, setSelectedRetailerId] = useState<number>();
    const [skuOptions, setSkuOptions] = useState<ProductDto[]>([]);
    const [retailerOptions, setRetailerOptions] = useState<RetailerDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [hasStartedSimulation, setHasStartedSimulation] = useState(false);

    const [searchProducts] = useLazySearchProductsQuery();
    const [searchRetailers] = useLazySearchRetailersQuery();

    const selectedRetailerOption = useMemo(
        () => retailerOptions.find(option => option.id === selectedRetailerId) ?? null,
        [selectedRetailerId, retailerOptions]
    );

    useEffect(() => {
        if (!isReadOnly) {
            return;
        }

        setSelectedSku(null);
        setSelectedRetailerId(selectedOptions?.retailer_id);
        setStep(2);
        setIsLoading(false);
        setLoadError(false);
    }, [isReadOnly, selectedOptions?.retailer_id]);

    useEffect(() => {
        if (isReadOnly || !initialOptions) {
            return;
        }

        let isMounted = true;

        const loadRetailersAsync = async (): Promise<void> => {
            setIsLoading(true);
            setLoadError(false);
            setSelectedSku(null);
            setSelectedRetailerId(undefined);
            setSkuOptions([]);
            setRetailerOptions([]);
            setStep(0);
            setHasStartedSimulation(false);

            try {
                const retailerName = initialOptions.retailers?.length
                    ? initialOptions.retailers[0].retailer_name
                    : "";

                const retailerResponse = await searchRetailers({
                    search_term: retailerName,
                    limit: 10
                }).unwrap();

                if (!isMounted) {
                    return;
                }

                setRetailerOptions(retailerResponse.items);

                if (retailerResponse.items.length === 1) {
                    setSelectedRetailerId(retailerResponse.items[0].id);
                }
            } catch {
                if (!isMounted) {
                    return;
                }

                setLoadError(true);
                setRetailerOptions([]);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadRetailersAsync();

        return () => {
            isMounted = false;
        };
    }, [initialOptions, isReadOnly, searchRetailers]);

    useEffect(() => {
        if (isReadOnly || !initialOptions) {
            return;
        }

        setSelectedSku(null);
        setSkuOptions([]);

        if (selectedRetailerId == null) {
            return;
        }

        let isMounted = true;

        const loadProductsAsync = async (): Promise<void> => {
            setIsLoading(true);
            setLoadError(false);

            try {
                const productResponse = await searchProducts({
                    search_term: initialOptions.sku_name ?? "",
                    retailer_id: selectedRetailerId,
                    limit: 10
                }).unwrap();

                if (!isMounted) {
                    return;
                }

                setSkuOptions(productResponse.items);

                if (productResponse.items.length === 1) {
                    setSelectedSku(productResponse.items[0]);
                    setStep(2);
                } else {
                    setStep(1);
                }
            } catch {
                if (!isMounted) {
                    return;
                }

                setLoadError(true);
                setSkuOptions([]);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadProductsAsync();

        return () => {
            isMounted = false;
        };
    }, [initialOptions, isReadOnly, searchProducts, selectedRetailerId]);

    if (!initialOptions && !isReadOnly) {
        return (
            <div className="alert alert-warning mb-0" role="alert">
                Unable to display tool call details due to invalid JSON content.
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="border rounded-3 p-3 bg-white">
                <p className="mb-0 text-muted">Loading matching options...</p>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="alert alert-danger mb-0" role="alert">
                Unable to load matching options.
            </div>
        );
    }

    const handleSkuChange = (product: ProductDto | null): void => {
        setSelectedSku(product);

        if (product != null) {
            setStep(2);
        }
    };

    const handleRetailerChange = (retailerId: number): void => {
        setSelectedRetailerId(retailerId);
        setSelectedSku(null);
        setSkuOptions([]);
    };

    const handleBack = (): void => {
        setStep(current => current === 2 ? 1 : 0);
    };

    const handleStartSimulation = (): void => {
        if (hasStartedSimulation || isBusy) {
            return;
        }

        if (selectedSku?.id == null || selectedRetailerOption?.id == null) {
            return;
        }

        setHasStartedSimulation(true);
        actions.startSimulationAsync({
            sku_id: selectedSku.id,
            sku_name: selectedSku.name,
            retailer_id: selectedRetailerOption.id,
            retailer_name: selectedRetailerOption.name
        }, message);
    };

    const handleOpenGeneratedPlan = (): void => {
        if (planResponse == null) {
            return;
        }

        dispatch(setCurrentCommercialPlan(planResponse));
    };

    const summarySkuName = isReadOnly
        ? selectedOptions?.sku_name
        : selectedSku?.name;

    const summaryRetailerName = isReadOnly
        ? selectedOptions?.retailer_name
        : selectedRetailerOption?.name;

    const showRetailerSelection = !isReadOnly && step === 0;
    const showSkuSelection = !isReadOnly && step === 1;
    const showFinalSummary = isReadOnly || step === 2;
    const canStartSimulation = selectedSku != null && selectedRetailerId != null && !isBusy;

    const primaryAction = step === 0
        ? {
                label: "Next",
                disabled: selectedRetailerId == null,
                themeColor: "success" as const,
                onClick: () => setStep(1)
            }
        : step === 1
            ? {
                    label: "Next",
                    disabled: selectedSku == null,
                    themeColor: "success" as const,
                    onClick: () => setStep(2)
                }
            : {
                    label: "Start Simulation",
                    disabled: !canStartSimulation,
                    themeColor: "primary" as const,
                    onClick: handleStartSimulation
                };

    return (
        <div className="border rounded-3 p-3 bg-white">
            {showSkuSelection && selectedRetailerId != null && (
                <SkuSelection
                    options={skuOptions}
                    retailerId={selectedRetailerId}
                    selectedProduct={selectedSku}
                    originalSkuName={initialOptions?.sku_name}
                    onChange={handleSkuChange}
                />
            )}

            {showRetailerSelection && (
                <RetailerSelection
                    options={retailerOptions}
                    selectedRetailerId={selectedRetailerId}
                    onChange={handleRetailerChange}
                />
            )}

            {showFinalSummary && (
                <FinalSummary
                    skuName={summarySkuName}
                    retailerName={summaryRetailerName}
                />
            )}

            <ActionButtons
                isReadOnly={isReadOnly}
                isPlanAvailable={planResponse != null}
                isBusy={isBusy}
                hasStartedSimulation={hasStartedSimulation}
                showBack={step > 0 && !isBusy && !isReadOnly}
                primaryAction={primaryAction}
                onBack={handleBack}
                onCancel={actions.cancelToolCallAsync}
                onOpenGeneratedPlan={handleOpenGeneratedPlan}
            />
        </div>
    );
};

export default PlanToolCallSteps;
